import { LitElement, html, css, PropertyValues } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { TimeZone } from './voy-tide-info-popup';

interface DataSerie
{
    name: String,
    type: String,
    smooth: Boolean,
    color: String,
    showSymbol: Boolean,
    hoverAnimation: Boolean,
    data: number[][],
    markLine?: any,
    id: string,
    symbolSize: number
}

@customElement('voy-tide-pdf-graph')
export class VoyTidePdfGraph extends LitElement {
    @property({type: Array}) tideData: number[][] = [];
    @property({type: Number}) fromDate: number = -1;
    @property({type: Number}) toDate: number = -1;
    @property({type: Object}) timeZone: TimeZone = { label: "UTC 0", offset: 0 };
    @property({type: Number}) minHeight: number = 2.5;

    private _chart: any;
    private _dataSeries: DataSerie[] | null = null;
    private _chartElement: HTMLDivElement | null = null;
    private _minHeightIntervals: any[] = [];
    
    constructor() {
        super();

        this._initialize = this._initialize.bind(this);
    }

    static get styles() {
        return [
            css`
            :host {
                width: 100%;
                height: 100%;
            }
    
            .graph {
                background: white;
                height: 500px;
                width: 1050px;
            }

            .container {
                font-family: 'MaerskText';
                display: grid;
                place-content: center;
            }
        `];
    }

    render() {
        return html`
            <div class="container" id="container-div">
                <div class="graph" id="plot"></div>
            </div>
        `;
    }

    getImage() {
        return this._chart.getDataURL({ backgroundColor: '#fff' });
    }

    updated(changedProperties: PropertyValues) {
        if (!this._chart) return;

        if (!this.tideData || !this.tideData.length) {
            return;
        }

        this._showChart();
        this._chart.resize();
        this._dataSeries![0].data = this.tideData;
        
        this._chart.setOption({
            series: this._dataSeries
        });

        this._setMarkLines();
    }

    private _setMarkLines() {
        if (!this.tideData) {
            return;
        }
        
        let data = Array.from(this.tideData);
        const intervals = [];
                
        for (let i = 0; i< data.length - 1; i++) {
            if (data[i][1] < this.minHeight && data[i+1][1] >= this.minHeight ) {
                intervals.push(data[i+1][0]);
            }
            if (data[i][1] > this.minHeight && data[i+1][1] <= this.minHeight ) {
                intervals.push(data[i][0]);
            }
        }

        this._minHeightIntervals = intervals.map(i => 
            { return { 
                        xAxis: i, 
                        label: { show: true,  
                                 formatter: (valueObject: any) => {
                                    const dateVal = new Date(valueObject.value + this.timeZone.offset) ;
                                    const timeOfDay = `${dateVal.getUTCHours().toString().padStart(2,'0')}:${dateVal.getUTCMinutes().toString().padStart(2,'0')}`;
                                    return `${timeOfDay}`;
                               }},
                        lineStyle: {
                            width: 2
                        }
                    }
        });

        this._chart.setOption({
            series: {
                id: "tideData",
                markLine: this._minHeightMarkLine(),
            }        
        });
    }

    private _minHeightMarkLine() {
        const computedStyle = getComputedStyle(this);
        return {
            data: [
                ...this._minHeightIntervals,
                { xAxis: Date.now() + this.timeZone.offset,
                  label: { show: true,  formatter: (valueObject: any) => {
                    return `Now ${this.timeZone.label}` ;
                  }},
                  lineStyle: {
                    width: 3,
                    color: computedStyle.getPropertyValue("--neutrals-grey-03"),
                  }
                },
                { yAxis: this.minHeight,
                  label: { show: true, position: "middle",  formatter: (valueObject: any) => {
                      return `Window of opportunity ${valueObject.value.toFixed(2)} m` ;
                  }},
                  lineStyle: {
                      width: 2
                  }
                },
                
            ],
            animation: false, symbol: ["none", "none"]
        };
    }

    get chart() : any {
        return this._chart;
    }

    _initialize() {
        this._dataSeries = [{
            name: 'Height',
            type: 'line',
            smooth: true,
            color: 'black',
            showSymbol: false,
            hoverAnimation: false,
            data: this.tideData,
            id: "tideData",
            symbolSize: 10
        }];
        
        this._chartElement = this.shadowRoot!.querySelector('#plot');
        
        if (!this._chart) {
            // @ts-ignore
            this._chart = echarts.init(this._chartElement);
        } else {
            this._chart.clear();
        }
        
        this._chart.setOption({
            title: {
                text: '',
            },
            textStyle: {
                color: 'black',
                fontSize: 10,
                fontFamily: 'MaerskText'
            },
            grid: {
                top: 25,
                bottom: 45,
                right: 30,
                left: 70
            },
            xAxis: {
                type: 'time',
                nameLocation: 'middle',
                nameTextStyle: {
                    color: 'black',
                    fontSize: 12,
                    fontFamily: 'MaerskText',
                },
                min: 'dataMin',
                axisLabel: {
                    showMinLabel: true,
                    showMaxLabel: true,
                    formatter: (value: number, index: any) => {
                        var date = new Date(value + this.timeZone.offset);
                        const month = date.toLocaleString('en-us', { month: 'short', timeZone: 'UTC' });
                        const timeOfDay = `${date.getUTCHours().toString().padStart(2,'0')}:${date.getUTCMinutes().toString().padStart(2,'0')}`;
                        return `${timeOfDay}\n${date.getUTCDate()} ${month}`;
                    }
                },
                name: "Time",
                splitNumber: 10
            },
            yAxis: {
                nameRotate: 90,
                nameLocation: 'middle',
                nameTextStyle: {
                    color: 'black',
                    fontSize: 12,
                    fontFamily: 'MaerskText',
                    lineHeight: 10
                },
                axisLabel: {
                    showMinLabel: true,
                    showMaxLabel: true,
                    margin: 8,
                },
                name: "Height in meters",
                splitNumber: 10,
                nameGap: 30,
                min: function (value: any) {
                    return (value.min - (value.max - value.min)*0.15).toFixed(2);
                },
                max: function (value: any) {
                    return (value.max + (value.max - value.min)*0.15).toFixed(2);
                },
            },
            responsive: true,
            maintainAspectRatio: false,
            series: this._dataSeries,
        })        
    }

    
    _showChart() {
        this._chartElement = this.shadowRoot!.querySelector('#plot');
        this._chartElement!.style.display = 'block';
    }

    firstUpdated() {
        this._initialize();
    }
};
